import { useState } from 'react';
import { X, AlertCircle, Trash2, Loader2 } from 'lucide-react';
import { doc, deleteDoc } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { auth, db } from '../../shared/firebase';
import { ListRow } from '../../shared/ui';

// Payment, commission, legal and sign-out now live on the Business Profile
// page itself; this sheet only holds account management (Delete Account).
export default function BusinessPreferencesSheet({ open, onClose }) {
  const { user } = useAuth();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const onDeleteAccount = async () => {
    if (!user) return;
    setBusy(true);
    setError(null);
    try {
      try {
        await deleteDoc(doc(db, 'users', user.uid));
      } catch (e) {
        console.warn('user doc delete failed:', e);
      }
      await auth.currentUser.delete();
      // Auth state change routes to /login automatically.
    } catch (e) {
      setBusy(false);
      setConfirmDelete(false);
      if (e.code === 'auth/requires-recent-login') {
        setError(
          'For security reasons, please sign out and sign back in before deleting your account.'
        );
      } else {
        setError(
          'Failed to delete account. Please try again or contact support.'
        );
      }
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 ${open ? 'block' : 'hidden'}`}
      onClick={onClose}
    >
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div
        className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-4 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 active:bg-gray-200"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-4 py-4 space-y-6">
          {/* Account Management */}
          <div>
            <p className="px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              Account Management
            </p>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden divide-y divide-gray-100">
              <ListRow
                icon={Trash2}
                label="Delete Account"
                destructive
                onClick={() => setConfirmDelete(true)}
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-100 rounded-xl p-4">
              <div className="flex gap-3">
                <AlertCircle size={18} className="text-red-500 shrink-0 mt-0.5" />
                <div className="text-sm text-red-700">{error}</div>
              </div>
            </div>
          )}
        </div>

        {/* Delete Account Confirmation */}
        {confirmDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-2xl max-w-sm w-full p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Account</h3>
              <p className="text-sm text-gray-600 mb-6">
                This action cannot be undone. Your account and all associated data including restaurant information, orders, and profile data will be permanently deleted.
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-gray-100 text-gray-700 font-semibold text-sm active:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onDeleteAccount}
                  disabled={busy}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 text-white font-semibold text-sm active:bg-red-600 disabled:opacity-50"
                >
                  {busy ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 size={16} className="animate-spin" />
                      Deleting...
                    </span>
                  ) : (
                    'Delete Account'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
